import React, {useRef} from 'react';
import { connect } from 'react-redux';
import {withStyles, Typography, useTheme} from '@material-ui/core';
import {hasPermission, formatDate} from '../../sc-utilities';
import DiagnoseList from "./DiagnoseList";
import {diagnoseCaseExecutionTypesLookup, diagnoseCaseGroupsLookup} from "../../sc-admin/AdminDiagnoseCase";
import moment from "moment";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const Diagnose = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, productID, ClientType } = props;

    const theme = useTheme();

    let visible = columns && columns['Diagnose'];

    const groupLookups = diagnoseCaseGroupsLookup(translations);
    const executionTypesLookups = diagnoseCaseExecutionTypesLookup(translations);

    const getFirstDiagnoseCaseRound = (rowData) => {
        if (rowData?.DiagnoseCaseRounds && rowData?.DiagnoseCaseRounds.length > 0) {
            return rowData?.DiagnoseCaseRounds[0];
        } return null;
    }

    const statusStyles = {
        started: {
            color: theme.palette.primary.main,
        },
        notavailable: {
            color: theme.palette.warning.dark,
        },
        inprogress: {
            color: theme.palette.customInProgress.dark,
        },
        aborted: {
            color: theme.palette.aborted.main,
        },
        succeeded: {
            color: theme.palette.success.dark,
        },
        failed: {
            color: theme.palette.error.dark,
        },
        notperformed: {
            color: theme.palette.customInProgress.dark
        }
    }

    const statusesLookup = {
        "notavailable": translations["not_supported"] || "Not supported",
        "aborted": translations["aborted"] || "Aborted",
        "succeeded": translations["succeeded"] || "Succeeded",
        "failed": translations["failed"] || "Failed",
        "notperformed": translations["diagnose_report_not_performed"] || "Not performed",
    };


    const getStatus = (status) => {
        let text = translations['diagnose_report_not_performed'] || 'Not Performed';
        let chosenStatus;

        switch (status?.toLowerCase()) {
            case 'succeeded':
            case 'notavailable':
            case 'failed':
            case 'aborted':
                chosenStatus = status?.toLowerCase() 
                break;
            case 'started':
            case 'inprogress':
            case 'notstarted':
                chosenStatus = 'notperformed';
                break;
            default:
                text = ' ';
                console.log(`Unknown status: ${status}`);

        }
        text = statusesLookup[chosenStatus] || translations[chosenStatus] || chosenStatus;
        const style = statusStyles[chosenStatus] ?? {};
        return <Typography variant={'caption'} style={style}>{status ? text : (translations['not_started'] || 'Not started')}</Typography>;
    }

    let tableColumns = [
        {
            title: translations["long_name_original"] || 'Long name (original)', field: 'LongName',
            hidden: visible ? visible['LongName'] : false,
            filtering: true,
            render: rowData => {
                const longName = rowData.LongName;
                const originalLongName = rowData.OriginalLongName;
                return <Typography variant={'caption'}>{longName ? `${longName} ${originalLongName ? `(${originalLongName})` : ""}` : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["short_name_original"] || 'Short name (original)', field: 'ShortName',
            hidden: visible ? visible['ShortName'] : false,
            filtering: true,
            render: rowData => {
                const shortName = rowData.Key && translations[`report_${rowData.Key}`] ? translations[`report_${rowData.Key}`] : rowData.ShortName || translations['short_not_assigned'] || 'n/a';
                const originalShortName = rowData.Key && translations[`report_${rowData.Key}`] ? translations[`report_${rowData.Key}`] : rowData.OriginalShortName || translations['short_not_assigned'] || 'n/a';
                return <Typography variant={'caption'}>{rowData.ShortName ? `${shortName} ${rowData.OriginalShortName ? `(${originalShortName})` : ""}` : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["execution_type"] || 'Execution type', field: 'ExecutionType',
            hidden: visible ? visible['ExecutionType'] : false,
            filtering: true,
            filterCellStyle: { maxWidth: '150px' },
            lookup: executionTypesLookups,
            defaultFilter: Object.keys(executionTypesLookups),
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ExecutionType ? translations[rowData.ExecutionType] || rowData.ExecutionType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["results"] || 'Results', field: 'Results',
            hidden: visible ? visible['Results'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{getFirstDiagnoseCaseRound(rowData)?.Results ||  translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["group"] || 'Group', field: 'Group',
            hidden: visible ? visible['Group'] : false,
            filtering: true,
            filterCellStyle: { maxWidth: '150px' },
            lookup: groupLookups,
            defaultFilter: Object.keys(groupLookups),
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Group ? translations[rowData.Group] || rowData.Group : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["visible"] || 'Visible', field: 'Visible',
            hidden: visible ? visible['Visible'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Visible ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["start"] || 'Start', field: 'Start',
            hidden: visible ? visible['Start'] : false,
            filtering: false,
            render: rowData => {
                const startDate = getFirstDiagnoseCaseRound(rowData)?.Start;
                return <Typography variant={'caption'}>{startDate? formatDate(startDate) : (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["end"] || 'End', field: 'End',
            hidden: visible ? visible['End'] : false,
            filtering: false,
            render: rowData => {
                const endDate = getFirstDiagnoseCaseRound(rowData)?.End;
                return <Typography variant={'caption'}>{endDate? formatDate(endDate) : (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["duration"] || 'Duration', field: 'Duration',
            hidden: visible ? visible['Duration'] : false,
            filtering: false,
            render: rowData => {
                const duration = getFirstDiagnoseCaseRound(rowData)?.Duration;
                return <Typography variant={'caption'}>{duration ?? (translations['short_not_assigned'] || 'n/a')}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            filterCellStyle: { maxWidth: '150px' },
            filtering: true,
            lookup: statusesLookup,
            defaultFilter: Object.keys(statusesLookup),
            render: rowData => {
                return getStatus(getFirstDiagnoseCaseRound(rowData)?.Status || null) || null;
            }
        }
    ];

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'diagnose_case_round_results_list') &&
                <DiagnoseList
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                    productID={productID}
                    ClientType={ClientType}
                />}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(Diagnose));
